import { memo } from "react";
import { MoreHorizontal, Flag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";

import { cn } from "@/lib/utils";
import type { AgentTimelineItem } from "@/hooks/useChat";
import { useSmoothText } from "@/hooks/useSmoothText";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { CopyButton } from "../../buttons/CopyButton";
import { MessageFrame } from "./MessageFrame";

const AgentMessageActions = memo(function MessageActions({
  text,
}: {
  text: string;
}) {
  return (
    <div className="mt-1.5 flex items-center gap-2 px-1">
      <CopyButton text={text} size="md" title="Copy message" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded text-zinc-600 transition-colors hover:bg-white/[0.05] hover:text-zinc-400"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem asChild className="text-zinc-400">
            <a href="mailto:hello@symbolica.ai">
              <Flag className="mr-2 h-3.5 w-3.5" />
              Report issue
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

export const AgentCard = memo(function AgentCard({
  item,
}: {
  item: AgentTimelineItem;
}) {
  const { text: streamedText } = useSmoothText(item.text);

  return (
    <MessageFrame side="bot">
      <div
        className={cn(
          "prose prose-invert prose-sm max-w-none text-[14px] leading-relaxed text-zinc-200 break-words",
          "prose-headings:font-semibold prose-headings:text-zinc-100 prose-headings:mt-4 prose-headings:mb-2",
          "prose-h1:text-lg prose-h2:text-base prose-h3:text-sm",
          "prose-p:my-2 prose-p:leading-relaxed",
          "prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline",
          "prose-strong:text-zinc-100 prose-em:text-zinc-300",
          "prose-pre:my-3 prose-pre:rounded-lg prose-pre:border prose-pre:border-white/[0.06] prose-pre:bg-zinc-900/80 prose-pre:p-3",
          "prose-code:rounded prose-code:bg-white/[0.06] prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[13px] prose-code:text-zinc-300 prose-code:font-normal",
          "prose-code:before:content-none prose-code:after:content-none",
          "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[12px]",
          "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-li:marker:text-zinc-500",
          "prose-blockquote:border-l-zinc-600 prose-blockquote:text-zinc-400 prose-blockquote:not-italic",
          "prose-th:text-zinc-300 prose-td:text-zinc-400",
          "prose-hr:border-white/[0.06]",
        )}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
        >
          {streamedText}
        </ReactMarkdown>
      </div>
      <AgentMessageActions text={item.text} />
    </MessageFrame>
  );
});
